'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useTenantAndVariant } from '@multitenancy/TenantAndVariantContext';

function FunStoreHero({ storeConfig }: { storeConfig: any }) {
  return (
    <div className="relative w-full">
      <section className="relative h-[100vh] min-h-[700px] w-full flex items-center justify-center overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #FF6B9D 0%, #FF8E53 25%, #FFD93D 50%, #FF6B9D 75%, #E91E63 100%)' }}>
        {/* Animated background shapes */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-[10%] left-[5%] text-[120px] md:text-[200px] opacity-10 animate-bounce-slow select-none">🐾</div>
          <div className="absolute top-[20%] right-[10%] text-[80px] md:text-[140px] opacity-10 animate-float select-none" style={{ animationDelay: '1s' }}>🐱</div>
          <div className="absolute bottom-[15%] left-[15%] text-[90px] md:text-[160px] opacity-10 animate-float select-none" style={{ animationDelay: '2s' }}>🐶</div>
          <div className="absolute bottom-[25%] right-[5%] text-[70px] md:text-[120px] opacity-10 animate-bounce-slow select-none" style={{ animationDelay: '0.5s' }}>🐾</div>
          <div className="absolute top-[50%] left-[40%] text-[60px] md:text-[100px] opacity-[0.07] animate-float select-none" style={{ animationDelay: '1.5s' }}>🐟</div>
          <div className="absolute top-[5%] left-[50%] text-[50px] md:text-[80px] opacity-[0.07] animate-bounce-slow select-none" style={{ animationDelay: '3s' }}>🦴</div>
        </div>

        {/* Soft overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30" />

        {/* Content */}
        <div className="relative z-10 text-center px-6 md:px-12 max-w-5xl">
          {/* Paw print accent */}
          <div className="flex items-center justify-center mb-8 animate-fade-in-down">
            <span className="text-4xl md:text-5xl">🐾</span>
          </div>

          <h1 className="text-5xl sm:text-6xl md:text-8xl lg:text-9xl font-thin text-white mb-6 animate-fade-in-up" style={{
            textShadow: '0 4px 30px rgba(0,0,0,0.3)',
            letterSpacing: '0.3em',
          }}>
            PETSELECT
          </h1>

          <div className="flex justify-center my-6 md:my-8 animate-fade-in-delayed">
            <div className="w-px h-12 md:h-16 bg-gradient-to-b from-transparent via-white/60 to-transparent" />
          </div>

          <p className="text-sm md:text-lg font-light text-white/90 mb-12 animate-fade-in-up-delayed" style={{
            textShadow: '0 2px 15px rgba(0,0,0,0.3)',
            letterSpacing: '0.4em',
          }}>
            PREMIUM PET PRODUCTS FOR YOUR FURRY FRIENDS
          </p>

          <div className="animate-fade-in-final">
            <Link
              href="/store"
              className="group relative inline-flex items-center justify-center overflow-hidden"
            >
              <span className="relative inline-flex items-center gap-4 bg-white/20 backdrop-blur-md border border-white/50 text-white px-12 md:px-16 py-4 md:py-5 text-[10px] md:text-xs font-medium transition-all duration-700 group-hover:bg-white group-hover:text-[#E91E63] group-hover:border-white group-hover:shadow-[0_0_50px_rgba(255,255,255,0.4)] group-hover:scale-105 rounded-full" style={{
                letterSpacing: '0.4em'
              }}>
                SHOP NOW
                <span className="text-lg opacity-80 group-hover:opacity-100 transition-opacity">🐾</span>
              </span>
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce-slow">
          <span className="text-white/60 text-xs tracking-[0.2em] uppercase">Scroll</span>
          <div className="w-px h-12 bg-gradient-to-b from-white/60 to-transparent" />
        </div>
      </section>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

export function HeroClassic() {
  const { featureToggles, storeConfig } = useTenantAndVariant();

  // Fun Store gets a special pet-themed gradient hero
  if (storeConfig.name === 'fun') {
    return <FunStoreHero storeConfig={storeConfig} />;
  }

  const heroImage = storeConfig.heroImage || '/images/hero.png';
  const heroSubtitle = storeConfig.heroSubtitle || 'DISCOVER QUALITY PRODUCTS';
  // Split display name into two lines for the hero layout
  const nameParts = storeConfig.displayName.split(/\s+/);
  const heroLine1 = nameParts.length > 1 ? nameParts.slice(0, -1).join(' ') : nameParts[0];
  const heroLine2 = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
  const isFocused = featureToggles.heroWidthMode === 'focused';
  const isLimited = featureToggles.heroWidthMode === 'limited' || isFocused;

  return (
    <div className={`relative w-full ${isFocused ? 'bg-black' : ''}`}>
      {/* Blurred background for focused mode */}
      {isFocused && (
        <div className="absolute inset-0 overflow-hidden">
          <Image
            src={heroImage}
            alt="Background"
            fill
            className="object-cover blur-2xl scale-110 opacity-60"
            priority
          />
        </div>
      )}

      <section className={`relative h-[100vh] min-h-[700px] w-full flex items-center justify-center overflow-hidden bg-black ${isLimited ? 'max-w-[1920px] mx-auto' : ''}`}>
        {/* Background Image with Ken Burns effect */}
        <div className="absolute inset-0">
          <Image
            src={heroImage}
            alt={storeConfig.displayName}
            fill
            className="object-cover animate-ken-burns"
            priority
          />
          {/* Sophisticated multi-layer gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/30 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/40" />
          <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-black/30" />
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-amber-400/60 to-transparent animate-shimmer" />
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-amber-400/40 to-transparent animate-shimmer-delayed" />

        {/* Floating decorative corner accents */}
        <div className="absolute top-12 left-12 md:top-16 md:left-16 w-24 h-24 md:w-32 md:h-32 border-t border-l border-white/30 animate-fade-in-slow" />
        <div className="absolute top-12 right-12 md:top-16 md:right-16 w-24 h-24 md:w-32 md:h-32 border-t border-r border-white/30 animate-fade-in-slow" />
        <div className="absolute bottom-12 left-12 md:bottom-16 md:left-16 w-24 h-24 md:w-32 md:h-32 border-b border-l border-white/30 animate-fade-in-slow" />
        <div className="absolute bottom-12 right-12 md:bottom-16 md:right-16 w-24 h-24 md:w-32 md:h-32 border-b border-r border-white/30 animate-fade-in-slow" />

        {/* Main Content - Asymmetric vertical positioning */}
        <div className="relative z-10 w-full px-6 md:px-12 flex flex-col items-center justify-center min-h-screen">
          {/* Top spacer for asymmetric layout */}
          <div className="flex-1 min-h-[10vh]" />

          <div className="text-center max-w-6xl">
            {/* Decorative top accent - minimal and modern */}
            <div className="flex items-center justify-center mb-12 md:mb-16 animate-fade-in-down">
              <div className="h-px w-8 bg-amber-400/40" />
              <div className="mx-3 w-1 h-1 bg-amber-400/60" />
              <div className="h-px w-8 bg-amber-400/40" />
            </div>

            {/* Main Heading - ultra modern spacing */}
            <h1 className="text-6xl sm:text-6xl md:text-8xl lg:text-9xl font-thin mb-6 md:mb-8 text-white animate-fade-in-up" style={{
              textShadow: '0 0 60px rgba(0,0,0,0.9), 0 20px 40px rgba(0,0,0,0.7)',
              letterSpacing: '0.4em',
              wordSpacing: '0.2em'
            }}>
              {heroLine1.toUpperCase()}
            </h1>

            {heroLine2 && (
            <>
            {/* Vertical line connector - fashion editorial style */}
            <div className="flex justify-center my-8 md:my-10 animate-fade-in-delayed">
              <div className="w-px h-16 md:h-20 bg-gradient-to-b from-transparent via-amber-400/60 to-transparent" />
            </div>

            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-thin mb-12 md:mb-16 text-white/95 animate-fade-in-up-delayed" style={{
              textShadow: '0 0 50px rgba(0,0,0,0.9), 0 15px 35px rgba(0,0,0,0.7)',
              letterSpacing: '0.45em'
            }}>
              {heroLine2.toUpperCase()}
            </h2>
            </>
            )}

            {/* Subtitle - offset positioning for asymmetry */}
            <div className="flex justify-center mb-16 md:mb-20 animate-fade-in-up-more-delayed">
              <div className="max-w-3xl relative">
                {/* Decorative left accent */}
                <div className="hidden md:block absolute left-0 top-1/2 -translate-y-1/2 -translate-x-20 w-12 h-px bg-gradient-to-r from-transparent to-white/20" />

                <p className="text-xs md:text-sm lg:text-base font-light text-white/90 px-8 md:px-12" style={{
                  textShadow: '0 4px 25px rgba(0,0,0,0.9)',
                  letterSpacing: '0.5em',
                  lineHeight: '1.8'
                }}>
                  {heroSubtitle}
                </p>

                {/* Decorative right accent */}
                <div className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 translate-x-20 w-12 h-px bg-gradient-to-l from-transparent to-white/20" />
              </div>
            </div>

            {/* CTA Button with elevated positioning */}
            <div className="animate-fade-in-final">
              <Link
                href="/store"
                className="group relative inline-flex items-center justify-center overflow-hidden"
              >
                {/* Shimmer effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out" />

                {/* Button content */}
                <span className="relative inline-flex items-center gap-4 bg-white/5 backdrop-blur-md border border-white/40 text-white px-12 md:px-16 py-4 md:py-5 text-[10px] md:text-xs font-medium transition-all duration-700 group-hover:bg-white group-hover:text-black group-hover:border-white group-hover:shadow-[0_0_50px_rgba(255,255,255,0.4)] group-hover:scale-105" style={{
                  letterSpacing: '0.4em'
                }}>
                  DISCOVER COLLECTION
                  <span className="text-xs md:text-sm opacity-60 group-hover:opacity-100 transition-opacity">→</span>
                </span>
              </Link>
            </div>
          </div>

          {/* Bottom spacer for asymmetric layout - more space at bottom */}
          <div className="flex-1 min-h-[15vh]" />
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce-slow">
          <span className="text-white/60 text-xs tracking-[0.2em] uppercase">Scroll</span>
          <div className="w-px h-12 bg-gradient-to-b from-white/60 to-transparent" />
        </div>
      </section>
    </div>
  );
}
